import { Rect, Textbox, FabricImage, Circle, Line as FabricLine, Polygon as FabricPolygon, FabricObject, Path as FabricPath, FabricText } from 'fabric';
import { useStudioStore } from '../stores/studioStore';
import { useMappingStore } from '../stores/mappingStore';
import { loadCustomFont, ensureVariantLoaded } from './fontLoader';
import { getShapePoints } from './shapePoints';
import { getDashArray } from './elementDefaults';
import { applyTextboxWidthPolicy, resolveTextIntent, applyTextTransform, decorationProps, getDescenderSafeHeight, ELEMENT_ID_KEY } from './textMeasurement';
import { resolveTextPathRef } from './fabricFactory';
import { computeFittedFontSize } from './textFitter';
import { fillConfigToFabric } from './fabricVisuals';
import { recordButtonSnapshot } from './buttonDiagnosticBuffer';
import { getSvgPrimaryFill } from './svgColorUtils';
import type { CanvasElement, TextElement, ShapeElement, ImageElement, ButtonElement, LineElement, PathElement, SvgElement } from '../types';

export function getEditAsValue(element: CanvasElement, property: string): string | null {
  const state = useStudioStore.getState();
  return state.getResolvedElementValue(element, property);
}

export function updateFabricObjectFromElement(obj: FabricObject, element: CanvasElement) {
  switch (element.type) {
    case 'text': {
      const textEl = element as TextElement;
      const resolvedText = getEditAsValue(element, 'textContent') ?? getEditAsValue(element, 'text');
      const resolvedColor = getEditAsValue(element, 'fontColor') ?? getEditAsValue(element, 'color');
      const displayText = resolvedText ?? textEl.text;
      const displayColor = resolvedColor ?? textEl.color;

      obj.set({ opacity: textEl.opacity });
      if (obj instanceof FabricText && !(obj instanceof Textbox)) {
        const updatedPathData = resolveTextPathRef(textEl, useStudioStore.getState().canvasState.elements) || textEl.pathData;
        if (updatedPathData) {
          const pStartOffset = textEl.textPath?.startOffset ?? textEl.pathStartOffset ?? 0;
          const pSide = textEl.textPath?.side ?? textEl.pathSide ?? 'left';
          const pAlign = textEl.textPath?.align ?? textEl.pathAlign ?? 'baseline';
          const pathObj = new FabricPath(updatedPathData, {
            fill: '',
            stroke: '',
            visible: false,
          });
          loadCustomFont(textEl.fontFamily)
            .then(() =>
              ensureVariantLoaded(textEl.fontFamily, textEl.fontWeight, textEl.fontStyle)
            )
            .then(() => {
              const resolvedPathTextFill = (() => {
                if (resolvedColor) return resolvedColor;
                if (textEl.textFill && textEl.textFill.type === 'gradient') {
                  return fillConfigToFabric(textEl.textFill, obj.width || 100, obj.height || 100);
                }
                return displayColor;
              })();

              obj.set({
                text: applyTextTransform(displayText, textEl.textTransform),
                fontFamily: textEl.fontFamily,
                fontSize: textEl.fontSize,
                fontWeight: String(textEl.fontWeight),
                fontStyle: textEl.fontStyle,
                fill: resolvedPathTextFill,
                path: pathObj,
                pathStartOffset: pStartOffset,
                pathSide: pSide,
                pathAlign: pAlign,
                ...decorationProps(textEl.textDecoration),
              });
              obj.initDimensions();
              obj.setCoords();
              obj.canvas?.requestRenderAll();
            });
        }
      } else if (obj instanceof Textbox) {
        const isEditing = obj.isEditing;
        const storeWidth = (element as TextElement).width;
        const storeHeight = (element as TextElement).height;
        const intent = resolveTextIntent(element as TextElement);
        const fitMode = textEl.textFitMode;
        const isCWT = fitMode === 'contain-within-textbox' || fitMode === 'contain-within-structure';

        loadCustomFont(textEl.fontFamily)
          .then(() =>
            ensureVariantLoaded(textEl.fontFamily, textEl.fontWeight, textEl.fontStyle)
          )
          .then(() => {
          const editAsActive = useStudioStore.getState().editAsBrandId != null;
          let effectiveFontSize = textEl.fontSize;

          if (isCWT && !isEditing && editAsActive && storeWidth > 0 && storeHeight > 0) {
            const fitResult = computeFittedFontSize({
              text: displayText,
              boxWidth: storeWidth,
              boxHeight: storeHeight,
              fontSize: textEl.fontSize,
              fontFamily: textEl.fontFamily,
              fontWeight: textEl.fontWeight,
              fontStyle: textEl.fontStyle,
              lineHeight: textEl.lineHeight,
              letterSpacing: textEl.letterSpacing,
              minFontSize: 8,
            });
            effectiveFontSize = fitResult.fittedFontSize;
          }

          const resolvedTextFill = (() => {
            if (resolvedColor) return resolvedColor;
            if (textEl.textFill && textEl.textFill.type === 'gradient') {
              return fillConfigToFabric(textEl.textFill, storeWidth || obj.width || 100, storeHeight || obj.height || 100);
            }
            return displayColor;
          })();

          obj.set({
            ...(isEditing ? {} : { text: applyTextTransform(displayText, textEl.textTransform) }),
            fontFamily: textEl.fontFamily,
            fontSize: effectiveFontSize,
            fontWeight: String(textEl.fontWeight),
            fontStyle: textEl.fontStyle,
            textAlign: textEl.textAlign,
            lineHeight: textEl.lineHeight,
            fill: resolvedTextFill,
            charSpacing: textEl.letterSpacing * 10,
            ...decorationProps(textEl.textDecoration),
          });

          obj.initDimensions();

          if (isCWT && !isEditing) {
            obj.set({ width: storeWidth, height: storeHeight });
          } else {
            if (storeWidth && storeWidth > 0 && !isEditing) {
              obj.set({ width: storeWidth });
            }

            if (!isEditing) {
              applyTextboxWidthPolicy(obj, intent);
              const elementId = (obj as any)[ELEMENT_ID_KEY] as string | undefined;
              if (elementId) {
                const w = obj.width ?? 0;
                const h = obj.height ?? 0;
                const widthDiff = Math.abs((storeWidth ?? 0) - w) > 1;
                const heightDiff =
                  Math.abs(((element as TextElement).height ?? 0) - h) > 1;
                const update: Partial<CanvasElement> = {};
                if (widthDiff) (update as Partial<TextElement>).width = w;
                if (heightDiff) (update as Partial<TextElement>).height = h;
                if (intent === 'auto') {
                  (update as Partial<TextElement>).widthIntent = 'manual';
                }
                if (Object.keys(update).length > 0) {
                  useStudioStore.getState().updateElement(elementId, update);
                }
              }
            }
          }

          obj.setCoords();
          obj.canvas?.requestRenderAll();
        });
      }
      break;
    }
    case 'shape': {
      const shapeEl = element as ShapeElement;
      const resolvedFill = getEditAsValue(element, 'fillColor') ?? getEditAsValue(element, 'fill');
      const resolvedStroke = getEditAsValue(element, 'strokeColor') ?? getEditAsValue(element, 'stroke');

      obj.set({
        fill: resolvedFill ?? shapeEl.fill,
        stroke: resolvedStroke ?? shapeEl.stroke,
        strokeWidth: shapeEl.strokeWidth,
        opacity: shapeEl.opacity * (shapeEl.fillOpacity ?? 1),
      });
      if (obj instanceof Circle) {
        obj.set({ radius: Math.min(shapeEl.width, shapeEl.height) / 2 });
      }
      if (obj instanceof Rect && shapeEl.borderRadius !== undefined) {
        obj.set({ rx: shapeEl.borderRadius, ry: shapeEl.borderRadius });
      }
      if (obj instanceof FabricPolygon) {
        const newPoints = getShapePoints(shapeEl.shapeType, shapeEl.width, shapeEl.height);
        if (newPoints) {
          (obj as any).points = newPoints;
        }
      }
      break;
    }
    case 'image': {
      const imgEl = element as ImageElement;
      if (obj instanceof FabricImage) {
        obj.set({
          scaleX: imgEl.width / (obj.width ?? imgEl.width),
          scaleY: imgEl.height / (obj.height ?? imgEl.height),
        });
      }
      break;
    }
    case 'line': {
      const lineEl = element as LineElement;
      const resolvedStroke =
        getEditAsValue(element, 'strokeColor') ??
        getEditAsValue(element, 'fillColor');
      if (obj instanceof FabricLine) {
        obj.set({
          stroke: resolvedStroke ?? lineEl.stroke,
          strokeWidth: lineEl.strokeWidth,
          opacity: lineEl.opacity,
          strokeDashArray: getDashArray(
            lineEl.strokeDashStyle || 'solid',
            lineEl.strokeWidth ?? 2
          ),
          strokeLineCap: lineEl.lineCap || 'round',
        });
      }
      break;
    }
    case 'button': {
      const btnEl = element as ButtonElement;
      const resolvedBtnText = getEditAsValue(element, 'textContent') ?? getEditAsValue(element, 'buttonLabel');
      const resolvedBtnColor = getEditAsValue(element, 'fontColor');
      const resolvedBtnFill = getEditAsValue(element, 'buttonFillColor');

      // DIAGNOSTIC — temporary
      console.log('[BUTTON-COLOR-DEBUG]', {
        elementId: btnEl.id,
        elementName: btnEl.name,
        editAsActive: useStudioStore.getState().editAsBrandId != null,
        editAsBrandId: useStudioStore.getState().editAsBrandId,
        editAsBrandDataKeys: Object.keys(useStudioStore.getState().editAsBrandData || {}),
        editAsBrandDataSample: useStudioStore.getState().editAsBrandData
          ? Object.entries(useStudioStore.getState().editAsBrandData!).slice(0, 20).map(([k, v]) => `${k}=${v}`)
          : null,
        mappingsForElement: useMappingStore.getState().elementMappings[btnEl.id],
        resolvedBtnFill,
        resolvedBtnColor,
        resolvedBtnText,
        finalFillWillBe: resolvedBtnFill ?? btnEl.backgroundColor ?? '#06b6d4',
        storedBackgroundColor: btnEl.backgroundColor,
      });
      // END DIAGNOSTIC

      const bg = (obj as any)._csButtonBg as Rect | undefined;
      const txt = (obj as any)._csButtonText as FabricText | undefined;

      if (bg) {
        const fillToWrite = resolvedBtnFill ?? btnEl.backgroundColor ?? '#06b6d4';
        const fillBeforeWrite = bg.fill;
        bg.set({
          fill: fillToWrite,
          rx: btnEl.borderRadius ?? 8,
          ry: btnEl.borderRadius ?? 8,
          width: btnEl.width || 180,
          height: btnEl.height || 48,
        });
        if (fillToWrite === 'transparent') {
          bg.set({
            stroke: 'rgba(6, 182, 212, 0.4)',
            strokeDashArray: [4, 4],
            strokeWidth: 1,
          });
        } else {
          bg.set({
            stroke: null,
            strokeDashArray: undefined,
            strokeWidth: 0,
          });
        }
        bg.dirty = true;

        // DIAGNOSTIC — temporary write-verification
        console.log('[BUTTON-BG-WRITE]', {
          timestamp: Date.now(),
          elementId: btnEl.id,
          elementName: btnEl.name,
          editAsActiveAtWrite: useStudioStore.getState().editAsBrandId != null,
          fillBeforeWrite,
          fillToWrite,
          fillAfterWrite: bg.fill,
          writeSucceeded: bg.fill === fillToWrite,
          bgRefIdentity: bg === (obj as any)._csButtonBg,
        });
        // END DIAGNOSTIC

        bg.canvas?.requestRenderAll();
      }
      if (txt) {
        const btnFamily = btnEl.fontFamily;
        const btnWeight = btnEl.fontWeight || 600;
        const btnStyle = (btnEl as unknown as { fontStyle?: string }).fontStyle || 'normal';
        loadCustomFont(btnFamily)
          .then(() => ensureVariantLoaded(btnFamily, btnWeight, btnStyle))
          .then(() => {
            txt.set({
              text: resolvedBtnText ?? (btnEl.text || 'Click Here'),
              fontSize: btnEl.fontSize || 16,
              fontFamily: btnFamily,
              fontWeight: String(btnWeight),
              fontStyle: btnStyle,
              fill: resolvedBtnColor ?? (btnEl.color || '#ffffff'),
            });

            // Cache-poisoning fix: font properties changed after variant load.
            // Without initDimensions() the label keeps stale (possibly
            // fallback-metric) dimensions forever — the sync path previously
            // could NEVER refresh them. Factory path already does this.
            if (typeof (txt as unknown as { initDimensions?: () => void }).initDimensions === 'function') {
              (txt as unknown as { initDimensions: () => void }).initDimensions();
            }
            txt.setCoords();
            obj.setCoords();

            // Phase F.2: Width is NEVER recomputed in sync path. bg width uses
            // btnEl.width verbatim. User-driven width changes flow through
            // PropertyPanel -> updateElement -> store -> sync -> here.
            try {
              const labelBounds = txt.getBoundingRect();
              const group = obj as unknown as { left?: number; top?: number; width?: number; height?: number };
              recordButtonSnapshot({
                timestamp: Date.now(),
                phase: 'sync-update',
                elementId: btnEl.id || '',
                text: String(txt.text || ''),
                textLen: String(txt.text || '').length,
                fontSize: btnEl.fontSize,
                fontFamily: btnFamily,
                fontWeight: btnWeight,
                paddingX: btnEl.paddingX,
                storedWidth: btnEl.width,
                storedHeight: btnEl.height,
                bgWidth: bg?.width,
                bgHeight: bg?.height,
                bgLeft: bg?.left,
                bgTop: bg?.top,
                labelWidth: txt.width,
                labelHeight: txt.height,
                labelLeft: txt.left,
                labelTop: txt.top,
                labelInkLeft: labelBounds.left,
                labelInkRight: labelBounds.left + labelBounds.width,
                groupLeft: group.left,
                groupTop: group.top,
                groupWidth: group.width,
                groupHeight: group.height,
                fitChanged: undefined,
                fitNewWidth: undefined,
                note: 'sync path; width from btnEl.width verbatim',
              });
            } catch { /* diagnostic only */ }

            txt.canvas?.requestRenderAll();
          });
      }
      break;
    }
    case 'svg': {
      const svgEl = element as SvgElement;
      if ((obj as any)._csIsSvg) {
        const computedScaleX = svgEl.width / (svgEl.originalWidth || 1);
        const computedScaleY = svgEl.height / (svgEl.originalHeight || 1);

        console.log('[SVG-RESIZE-SYNC]', {
          stage: 'fabricSync-updateFabricObjectFromElement-svg-case',
          elementId: (obj as any).__elementId ?? 'unknown',
          fabric_scaleX_before: obj.scaleX,
          fabric_scaleY_before: obj.scaleY,
          fabric_width_before: obj.width,
          fabric_height_before: obj.height,
          store_width: svgEl.width,
          store_height: svgEl.height,
          store_originalWidth: svgEl.originalWidth,
          store_originalHeight: svgEl.originalHeight,
          computed_scaleX_write: computedScaleX,
          computed_scaleY_write: computedScaleY,
          formula: 'scaleX = svgEl.width / svgEl.originalWidth',
        });

        obj.set({
          scaleX: computedScaleX,
          scaleY: computedScaleY,
          opacity: svgEl.opacity ?? 1,
          lockUniScaling: svgEl.preserveAspectRatio,
        });
        applySvgFillResolution(obj, svgEl);
      }
      break;
    }
  }
  obj.setCoords();
}

const SVG_ORIGINAL_FILLS_KEY = '_csOriginalChildFills';

function normalizeHex(c: string): string {
  return c.trim().toLowerCase();
}

export function applySvgFillResolution(obj: FabricObject, svgEl: SvgElement): void {
  if (!(obj as unknown as Record<string, unknown>)._csIsSvg) return;
  const getChildren = (obj as unknown as { getObjects?: () => FabricObject[] }).getObjects;
  if (typeof getChildren !== 'function') return;
  const children = getChildren.call(obj);

  const resolvedFill = getEditAsValue(svgEl, 'fillColor');
  const record = obj as unknown as Record<string, unknown>;

  if (resolvedFill) {
    const primaryFill = getSvgPrimaryFill(svgEl);
    if (!primaryFill) return;

    if (!record[SVG_ORIGINAL_FILLS_KEY]) {
      record[SVG_ORIGINAL_FILLS_KEY] = children.map((child) => {
        const f = (child as unknown as { fill?: unknown }).fill;
        return typeof f === 'string' ? f : null;
      });
    }
    const snapshot = record[SVG_ORIGINAL_FILLS_KEY] as Array<string | null>;
    const target = normalizeHex(primaryFill);

    children.forEach((child, i) => {
      const original = snapshot[i];
      if (original && normalizeHex(original) === target) {
        child.set({ fill: resolvedFill });
      }
    });
    (obj as unknown as { dirty?: boolean }).dirty = true;
  } else if (record[SVG_ORIGINAL_FILLS_KEY]) {
    const snapshot = record[SVG_ORIGINAL_FILLS_KEY] as Array<string | null>;
    children.forEach((child, i) => {
      const original = snapshot[i];
      if (original !== null && original !== undefined) {
        child.set({ fill: original });
      }
    });
    delete record[SVG_ORIGINAL_FILLS_KEY];
    (obj as unknown as { dirty?: boolean }).dirty = true;
  }
}
